import { useEffect, useState } from "react";
import { bookQueue, getPublicConfig } from "../api";
import { Layout } from "../components/Layout";
import { isValidThaiMobile, normalizeThaiMobile } from "../phone";
import type { PublicConfig } from "../types";

type PageState = "loading" | "ready" | "friend-required" | "error";

export function BookingPage() {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [state, setState] = useState<PageState>("loading");
  const [displayName, setDisplayName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [message, setMessage] = useState("กำลังเชื่อมต่อกับ LINE…");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;

    async function initialize() {
      try {
        const publicConfig = await getPublicConfig();
        if (!active) return;
        setConfig(publicConfig);

        if (!window.liff) throw new Error("โหลด LINE LIFF SDK ไม่สำเร็จ");
        await window.liff.init({ liffId: publicConfig.liffId });
        if (!window.liff.isLoggedIn()) {
          window.liff.login({ redirectUri: window.location.href });
          return;
        }

        const [profile, friendship] = await Promise.all([
          window.liff.getProfile(),
          window.liff.getFriendship(),
        ]);
        if (!active) return;
        setDisplayName(profile.displayName);
        if (!friendship.friendFlag) {
          setState("friend-required");
          setMessage("เพิ่มบัญชีทางการเป็นเพื่อนก่อน เพื่อให้เราแจ้งเมื่อถึงคิว");
          return;
        }
        setState("ready");
        setMessage("พร้อมรับบัตรคิวแล้ว");
      } catch (error) {
        if (!active) return;
        setState("error");
        setMessage(error instanceof Error ? error.message : "เชื่อมต่อ LINE ไม่สำเร็จ");
      }
    }

    void initialize();
    return () => {
      active = false;
    };
  }, []);

  async function handleBook() {
    setPhoneTouched(true);
    const normalizedPhone = normalizeThaiMobile(phoneNumber);
    if (!isValidThaiMobile(normalizedPhone)) {
      setMessage("กรุณากรอกเบอร์มือถือไทย 10 หลักให้ถูกต้อง");
      return;
    }

    const idToken = window.liff?.getIDToken();
    if (!idToken) {
      setState("error");
      setMessage("ไม่พบ LINE ID token กรุณาเปิดหน้านี้ผ่าน LINE อีกครั้ง");
      return;
    }

    setSubmitting(true);
    try {
      const result = await bookQueue(idToken, normalizedPhone);
      window.location.replace(`/ticket/${result.ticketToken}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "จองคิวไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout compact>
      <section className="hero">
        <div className="eyebrow">QUEUE WITHOUT THE CROWD</div>
        <h1>รับคิว แล้วใช้เวลา<br />ของคุณได้เต็มที่</h1>
        <p>ไม่ต้องยืนรอหน้าจุดบริการ เราจะแจ้งผ่าน LINE ทันทีเมื่อถึงคิว</p>
      </section>

      <section className="booking-card">
        <div className="booking-card__accent" />
        {state === "loading" ? (
          <div className="loading-block">
            <span className="spinner" />
            <strong>{message}</strong>
            <small>ใช้เวลาเพียงครู่เดียว</small>
          </div>
        ) : (
          <>
            <div className="customer-row">
              <span className="avatar">{displayName ? displayName.slice(0, 1) : "L"}</span>
              <span>
                <small>ผู้รับคิว</small>
                <strong>{displayName || "ผู้ใช้ LINE"}</strong>
              </span>
            </div>
            <div className={`notice ${state === "ready" ? "notice--success" : ""}`}>
              <span>{state === "ready" ? "✓" : "!"}</span>
              <p>{message}</p>
            </div>

            <label className="booking-field">
              เบอร์โทรศัพท์
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                onBlur={() => setPhoneTouched(true)}
                placeholder="เช่น 0812345678"
                aria-invalid={phoneTouched && !isValidThaiMobile(phoneNumber)}
                required
              />
              {phoneTouched && !isValidThaiMobile(phoneNumber) ? (
                <span className="field-error">กรอกเบอร์มือถือไทย 10 หลัก เช่น 0812345678</span>
              ) : (
                <small>ใช้สำหรับให้เจ้าหน้าที่ติดต่อเกี่ยวกับคิวนี้</small>
              )}
            </label>

            {state === "friend-required" && config ? (
              <a className="primary-button" href={config.addFriendUrl}>
                เพิ่มเพื่อนใน LINE
              </a>
            ) : (
              <button
                className="primary-button"
                onClick={handleBook}
                disabled={
                  state !== "ready"
                  || submitting
                  || !isValidThaiMobile(phoneNumber)
                }
              >
                {submitting ? "กำลังออกบัตรคิว…" : "รับบัตรคิว"}
              </button>
            )}
            <p className="privacy-note">
              ระบบใช้ LINE ID ชื่อโปรไฟล์ และเบอร์โทรศัพท์เพื่อจัดการและแจ้งคิว
            </p>
          </>
        )}
      </section>

      <section className="steps">
        <div><b>01</b><span>รับหมายเลขคิว</span></div>
        <div><b>02</b><span>เก็บ QR ของคุณ</span></div>
        <div><b>03</b><span>รอแจ้งเตือนใน LINE</span></div>
      </section>
    </Layout>
  );
}
